const audioStreamContexts = {};

export function openAudioStream(
  uniqueStreamId,
  bufferSize,
  channelsCount,
  sampleRate,
  onAudioInputCallback
) {
  console.log(
    `Audio stream ${uniqueStreamId}: opening audio stream, bufferSize: ${bufferSize}, channelsCount: ${channelsCount}, sampleRate: ${sampleRate}`
  );
  const AudioContext = window.AudioContext || window.webkitAudioContext;

  //return navigator.mediaDevices.getUserMedia({ video: false, audio: {sampleRate: sampleRate} }).then((stream) => {
  return navigator.mediaDevices
    .getUserMedia({ audio: true })
    .then((stream) => {
      console.log(`Audio stream ${uniqueStreamId}: Audio stream opened, creating sources`);

      const context = new AudioContext();
      const source = context.createMediaStreamSource(stream);
      const processor = context.createScriptProcessor(bufferSize, channelsCount, channelsCount);

      processor.onaudioprocess = function(e) {
        onAudioInputCallback(e.inputBuffer);
      };

      audioStreamContexts[uniqueStreamId] = {
        context,
        source,
        processor,
        stream,
        recording: {
          channelsCount: channelsCount,
          channel0: [],
          channel1: [],
          length: 0,
          sampleRate: context.sampleRate,
        },
      };

      source.connect(processor);
      processor.connect(context.destination);

      console.log(
        `Audio stream ${uniqueStreamId}: opened, sampleRate: ${
          context.sampleRate
        }, audio streams count: ${Object.keys(audioStreamContexts).length}`
      );

      return audioStreamContexts[uniqueStreamId];
    })
    .then((r) => r);
}

export function closeAudioStream(uniqueStreamId) {
  console.log(`Audio stream ${uniqueStreamId}: closing audio stream`);
  const audioStreamContext = audioStreamContexts[uniqueStreamId];
  if (audioStreamContext) {
    delete audioStreamContexts[uniqueStreamId];

    audioStreamContext.stream.getTracks().forEach(function(track) {
      track.stop();
    });

    try {
      audioStreamContext.processor.disconnect();
      audioStreamContext.processor.disconnect(audioStreamContext.context.destination);
    } catch (error) {
      console.error(error);
    }

    try {
      audioStreamContext.source.disconnect();
    } catch (error) {
      console.error(error);
    }

    try {
      audioStreamContext.context.close();
    } catch (error) {
      console.error(error);
    }

    return Promise.resolve(audioStreamContext['recording']);
  } else {
    console.log(`Audio stream ${uniqueStreamId}: stream not found or already closed`);
  }

  console.log(
    `Audio stream ${uniqueStreamId}: opened audio streams count: ${
      Object.keys(audioStreamContexts).length
    }`
  );

  return Promise.resolve(null);
}

export function getRecordingContext(uniqueStreamId) {
  const audioStreamContext = audioStreamContexts[uniqueStreamId];
  return audioStreamContext;
}

export function addRecordingFromAudioBuffer(uniqueStreamId, audioBuffer) {
  const audioStreamContext = audioStreamContexts[uniqueStreamId];
  if (audioStreamContext) {
    collectAudioBuffers(audioStreamContext.recording, audioBuffer);
  }
}

export function collectAudioBuffers(recording, audioBuffer) {
  recording.channel0.push(new Float32Array(audioBuffer.getChannelData(0)));

  if (recording.channelsCount > 1) {
    recording.channel1.push(new Float32Array(audioBuffer.getChannelData(1)));
  }

  recording.length += audioBuffer.getChannelData(0).length;
}

export function mergeAndInterleaveRecording(recording) {
  const channel0 = recording.channel0;
  const channel1 = recording.channel1;

  const channel0Buffer = mergeBuffers(channel0);
  let interleaved;
  if (recording.channel1.length > 0) {
    const channel1Buffer = mergeBuffers(channel1);
    interleaved = interleave(channel0Buffer, channel1Buffer);
  } else {
    interleaved = channel0Buffer;
  }

  return interleaved;
}

export function interleave(leftChannel, rightChannel) {
  var length = leftChannel.length + rightChannel.length;
  var result = new Float32Array(length);
  var inputIndex = 0;

  for (var index = 0; index < length; ) {
    result[index++] = leftChannel[inputIndex];
    result[index++] = rightChannel[inputIndex];
    inputIndex++;
  }

  return result;
}

export function mergeBuffers(channelBuffers) {
  let recordingLength = 0;
  for (const channelBuffer of channelBuffers) {
    recordingLength += channelBuffer.length;
  }

  var result = new Float32Array(recordingLength);
  var offset = 0;
  for (const channelBuffer of channelBuffers) {
    result.set(channelBuffer, offset);
    offset += channelBuffer.length;
  }

  return result;
}

// adapted from https://gist.github.com/also/900023
// returns Uint8Array of WAV header bytes

function getWavHeader(options) {
  const numFrames = options.numFrames;
  const numChannels = options.numChannels || 2;
  const sampleRate = options.sampleRate || 44100;
  const bytesPerSample = options.isFloat ? 4 : 2;
  const format = options.isFloat ? 3 : 1;

  const blockAlign = numChannels * bytesPerSample;
  const byteRate = sampleRate * blockAlign;
  const dataSize = numFrames * blockAlign;

  const buffer = new ArrayBuffer(44);
  const dv = new DataView(buffer);

  let p = 0;

  function writeString(s) {
    for (let i = 0; i < s.length; i++) {
      dv.setUint8(p + i, s.charCodeAt(i));
    }

    p += s.length;
  }

  function writeUint32(d) {
    dv.setUint32(p, d, true);
    p += 4;
  }

  function writeUint16(d) {
    dv.setUint16(p, d, true);
    p += 2;
  }

  writeString('RIFF'); // ChunkID
  writeUint32(dataSize + 36); // ChunkSize
  writeString('WAVE'); // Format
  writeString('fmt '); // Subchunk1ID
  writeUint32(16); // Subchunk1Size
  writeUint16(format); // AudioFormat https://i.stack.imgur.com/BuSmb.png
  writeUint16(numChannels); // NumChannels
  writeUint32(sampleRate); // SampleRate
  writeUint32(byteRate); // ByteRate
  writeUint16(blockAlign); // BlockAlign
  writeUint16(bytesPerSample * 8); // BitsPerSample
  writeString('data'); // Subchunk2ID
  writeUint32(dataSize); // Subchunk2Size

  return new Uint8Array(buffer);
}

// Returns Uint8Array of WAV bytes
function getWavBytes(buffer, options) {
  const type = options.isFloat ? Float32Array : Uint16Array;
  const numFrames = buffer.byteLength / type.BYTES_PER_ELEMENT;

  const headerBytes = getWavHeader(Object.assign({}, options, { numFrames }));
  const wavBytes = new Uint8Array(headerBytes.length + buffer.byteLength);

  // prepend header, then add pcmBytes
  wavBytes.set(headerBytes, 0);
  wavBytes.set(new Uint8Array(buffer), headerBytes.length);

  return wavBytes;
}

export function createWavBytes(uniqueStreamId, recording) {
  if (recording.length == 0) {
    return null;
  }

  console.log(
    `Audio stream ${uniqueStreamId}: creating wav bytes for recording of length ${recording.length}.`
  );

  // const channel0 = recording.channel0;
  // const channel1 = recording.channel1;
  const channelsCount = recording.channelsCount;
  const sampleRate = recording.sampleRate;

  const interleaved = mergeAndInterleaveRecording(recording);

  // get WAV file bytes and audio params of your audio source
  const wavBytes = getWavBytes(interleaved.buffer, {
    isFloat: true, // floating point or 16-bit integer
    numChannels: channelsCount,
    sampleRate: sampleRate,
  });

  return wavBytes;
}

export function convertFloatTo16BitPCM(input) {
  var buffer = new ArrayBuffer(input.length * 2);
  var view = new DataView(buffer);

  for (var i = 0; i < input.length; i++) {
    var s = Math.max(-1, Math.min(1, input[i]));
    view.setInt16(i * 2, s < 0 ? s * 0x8000 : s * 0x7fff, true);
  }

  return buffer;
}
